/**
 * Lightweight, privacy-preserving bot & spam filtering for analytics.
 * Runs entirely in the browser — no IP checks, no fingerprinting.
 */

const BOT_UA =
  /(bot|crawler|spider|crawling|slurp|mediapartners|facebookexternalhit|embedly|quora link preview|showyoubot|outbrain|pinterest|vkshare|w3c_validator|whatsapp|telegrambot|discordbot|slackbot|twitterbot|linkedinbot|applebot|bingpreview|yandex|baiduspider|duckduckbot|semrush|ahrefs|mj12bot|dotbot|petalbot|headlesschrome|phantomjs|puppeteer|playwright|lighthouse|gtmetrix|pingdom|uptimerobot|curl|wget|python-requests|axios|node-fetch|go-http-client|java\/|okhttp|postman)/i;

/** True when the current visitor looks like a bot, crawler or headless agent. */
export function isLikelyBot(): boolean {
  if (typeof navigator === "undefined") return true;
  try {
    const ua = navigator.userAgent || "";
    if (!ua) return true;
    if (BOT_UA.test(ua)) return true;
    // Explicit automation flag exposed by most drivers.
    if ((navigator as Navigator & { webdriver?: boolean }).webdriver === true) return true;
    // Headless browsers commonly report zero languages / zero-size screens.
    if (Array.isArray(navigator.languages) && navigator.languages.length === 0) return true;
    if (typeof screen !== "undefined" && (screen.width === 0 || screen.height === 0)) return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * Debounce / rate-limit guard against refresh storms and event spam.
 * Keyed in sessionStorage so it survives reloads within a tab but leaves
 * no long-lived storage behind.
 */
export function rateLimit(key: string, windowMs: number): boolean {
  if (typeof window === "undefined") return false;
  const storageKey = `sfn.rl.${key}`;
  try {
    const now = Date.now();
    const last = Number(window.sessionStorage.getItem(storageKey) || 0);
    if (last && now - last < windowMs) return false;
    window.sessionStorage.setItem(storageKey, String(now));
    return true;
  } catch {
    // Storage blocked (private mode / cookies off) — allow, but only in memory.
    const mem = (globalThis as { __sfnRl?: Record<string, number> }).__sfnRl ?? {};
    (globalThis as { __sfnRl?: Record<string, number> }).__sfnRl = mem;
    const now = Date.now();
    if (mem[storageKey] && now - mem[storageKey] < windowMs) return false;
    mem[storageKey] = now;
    return true;
  }
}

/** Minimum gap between two page views of the SAME path in one tab. */
export const PAGE_VIEW_WINDOW_MS = 30_000;
/** Minimum gap between two identical interaction events. */
export const EVENT_WINDOW_MS = 1_000;
/** Hard cap on events sent per tab session (anti-spam). */
export const SESSION_EVENT_CAP = 300;

export function underSessionCap(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const n = Number(window.sessionStorage.getItem("sfn.evt.count") || 0) + 1;
    window.sessionStorage.setItem("sfn.evt.count", String(n));
    return n <= SESSION_EVENT_CAP;
  } catch {
    return true;
  }
}
